
import { Language, LessonContent, LessonWidget, SubjectType } from "../../types";

// --- Subject Specific Generators ---

const generateMathContent = (topic: string, isZh: boolean): LessonWidget[] => {
  return [
    { 
      type: 'magic_formula', 
      data: isZh ? `${topic} = 魔法 + 逻辑` : `${topic} = Magic + Logic` 
    },
    { 
      type: 'text', 
      data: isZh 
        ? `在连接建立之前，我们先来练习一下 ${topic} 的基本法则。` 
        : `While we connect to the Star Brain, let's practice the basic laws of ${topic}.` 
    },
    { 
      type: 'step_solver', 
      data: {
        problem: isZh ? `如何理解 ${topic}?` : `How to understand ${topic}?`,
        steps: isZh 
          ? ["第一步：观察模式", "第二步：建立假设", "第三步：验证答案"] 
          : ["Step 1: Observe Pattern", "Step 2: Hypothesize", "Step 3: Verify"]
      } 
    }
  ];
};

const generateLanguageContent = (topic: string, isZh: boolean): LessonWidget[] => {
  return [
    { 
      type: 'vocab_card', 
      data: { 
        word: "Discovery", 
        pronunciation: "dɪˈskʌvəri", 
        meaning: isZh ? "发现 / 探索" : "The act of finding something new" 
      } 
    },
    { 
      type: 'voice_practice', 
      data: { 
        phrase: "I love learning everyday.", 
        translation: isZh ? "我喜欢每天学习。" : "Use this phrase to express passion.", 
        tip: "Stress the word 'love'!" 
      } 
    },
    { 
      type: 'comic_strip', 
      data: { 
        panels: [
          { desc: "Two friends talking", speech: `Do you know ${topic}?` },
          { desc: "Friend explaining", speech: "It sounds amazing!" }
        ] 
      } 
    }
  ];
};

const generateScienceContent = (topic: string, isZh: boolean): LessonWidget[] => {
  return [
    { 
      type: 'visual_image', 
      data: { 
        desc: `Detailed scientific diagram of ${topic}, 3d render, educational`, 
        caption: isZh ? `${topic} 的结构剖析` : `Structure of ${topic}` 
      } 
    },
    { 
      type: 'did_you_know', 
      data: isZh 
        ? `你知道吗？${topic} 在宇宙中无处不在，甚至在你自己的身体里！` 
        : `Did you know? ${topic} is everywhere in the universe, even inside your body!` 
    }
  ];
};

const generateHistoryContent = (topic: string, isZh: boolean): LessonWidget[] => {
  return [
    { 
      type: 'visual_image', 
      data: { 
        desc: `Historical scene reconstruction of ${topic}, cinematic lighting`, 
        caption: isZh ? `重现 ${topic} 的历史现场` : `Reconstructing ${topic}` 
      } 
    },
    { 
      type: 'text', 
      data: isZh 
        ? `让我们穿越回过去，看看 ${topic} 是如何改变人类历史进程的。` 
        : `Let's travel back in time to see how ${topic} changed the course of human history.` 
    }
  ];
};

// --- Main Factory Function ---

export const getOfflineLesson = (topic: string, subject: SubjectType, lang: Language): LessonContent => {
  const isZh = lang === 'zh';
  
  let specificWidgets: LessonWidget[] = [];
  
  switch (subject) {
    case 'math': specificWidgets = generateMathContent(topic, isZh); break;
    case 'chinese':
    case 'english': specificWidgets = generateLanguageContent(topic, isZh); break;
    case 'science': specificWidgets = generateScienceContent(topic, isZh); break;
    case 'history':
    case 'art': specificWidgets = generateHistoryContent(topic, isZh); break;
    default: specificWidgets = generateScienceContent(topic, isZh);
  }

  return {
    mode: 'study',
    title: topic,
    category: subject,
    intro_story: isZh 
      ? `(离线模拟) 欢迎来到"${topic}"的奇妙世界！虽然我们暂时无法连接到星际大脑，但诺诺已经为你准备了本地全息投影。`
      : `(Offline Sim) Welcome to the world of "${topic}"! While we can't connect to the Star Brain right now, Nono has prepared a local hologram for you.`,
    core_concept: isZh 
      ? `${topic} 是该学科的一个核心概念。`
      : `${topic} is a core concept in this subject.`,
    widgets: specificWidgets,
    quiz: [
      { 
        q: isZh ? `关于 ${topic}，以下哪项是正确的？` : `Which is true about ${topic}?`, 
        options: isZh ? ["它很有趣", "它不存在"] : ["It is interesting", "It does not exist"], 
        a: 0 
      }
    ],
    mission: "Offline Exploration"
  };
};

export const getOfflineChatReply = (msg: string, lang: Language) => {
  const isZh = lang === 'zh';
  const responses = isZh ? [
    "星际信号有点弱，但我还在！你想聊聊今天的课程吗？",
    "我的主脑正在休眠，但我本地的处理器觉得你很棒！",
    "这是一个非常有趣的问题！等我们连上星际网络，我能告诉你更多。",
  ] : [
    "Star signal is weak, but I'm here! Want to discuss today's lesson?",
    "My main brain is sleeping, but my local processor thinks you're great!",
    "That's an interesting question! I can tell you more once we reconnect.",
  ];
  return responses[Math.floor(Math.random() * responses.length)];
};
